import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const Preloader = ({ onDone, onStartOpening }) => {
  const [opening, setOpening] = useState(false);
  const audioRef = useRef(null);
  const hasPlayedRef = useRef(false);

  // Play audio at 0.5x playback rate (stretching the 3s clip across the 5s door opening)
  const playDoorAudio = () => {
    if (!audioRef.current || hasPlayedRef.current || (typeof document !== "undefined" && document.hidden)) return;
    const audio = audioRef.current;
    try {
      audio.playbackRate = 0.5;
      audio.volume = 0.17;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            hasPlayedRef.current = true;
          })
          .catch((err) => {
            console.warn("Autoplay deferred until user interaction:", err.message);
          });
      }
    } catch (e) {
      console.warn("Audio play error:", e);
    }
  };

  useEffect(() => {
    // Initialize audio instance with 0.5x speed and 50% reduced volume (0.17)
    const audio = new Audio("/audio/freesound_community-restaurant-bell-101396.mp3");
    audio.preload = "auto";
    audio.defaultPlaybackRate = 0.5;
    audio.playbackRate = 0.5;
    audio.volume = 0.17;

    const enforceHalfSpeed = () => {
      audio.playbackRate = 0.5;
    };
    audio.addEventListener("loadedmetadata", enforceHalfSpeed);
    audio.addEventListener("canplay", enforceHalfSpeed);
    audio.addEventListener("play", enforceHalfSpeed);
    audioRef.current = audio;

    // Immediately attempt automatic playback as page loads
    playDoorAudio();

    const triggerDoorsOpened = () => {
      if (!window.__veeduDoorsOpened) {
        window.__veeduDoorsOpened = true;
        window.dispatchEvent(new CustomEvent("veedu:doors-opened"));
      }
    };

    // Start door opening after initial reveal (1.0s) and trigger ambient music simultaneously
    const timerStart = setTimeout(() => {
      setOpening(true);
      if (onStartOpening) onStartOpening();
      triggerDoorsOpened();
      playDoorAudio();
    }, 1000);

    // Complete transition after door opening animation
    const timerDone = setTimeout(() => {
      triggerDoorsOpened();
      onDone();
    }, 6000);

    // If browser's autoplay policy deferred initial play, unlock on any interaction
    const handlePassiveUnlock = () => {
      if (!hasPlayedRef.current && audioRef.current) {
        playDoorAudio();
      }
      triggerDoorsOpened();
    };
    window.addEventListener("pointerdown", handlePassiveUnlock, { once: true, capture: true });
    window.addEventListener("keydown", handlePassiveUnlock, { once: true, capture: true });
    window.addEventListener("wheel", handlePassiveUnlock, { once: true, capture: true });
    window.addEventListener("touchstart", handlePassiveUnlock, { once: true, capture: true });

    const handleVisibility = () => {
      if (document.hidden && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimeout(timerStart);
      clearTimeout(timerDone);
      window.removeEventListener("pointerdown", handlePassiveUnlock, { capture: true });
      window.removeEventListener("keydown", handlePassiveUnlock, { capture: true });
      window.removeEventListener("wheel", handlePassiveUnlock, { capture: true });
      window.removeEventListener("touchstart", handlePassiveUnlock, { capture: true });
      document.removeEventListener("visibilitychange", handleVisibility);
      audio.removeEventListener("loadedmetadata", enforceHalfSpeed);
      audio.removeEventListener("canplay", enforceHalfSpeed);
      audio.removeEventListener("play", enforceHalfSpeed);

      // Smooth fade-out on unmount
      try {
        let vol = audio.volume;
        const fadeInterval = setInterval(() => {
          vol = Math.max(0, vol - 0.15);
          audio.volume = vol;
          if (vol <= 0) {
            clearInterval(fadeInterval);
            audio.pause();
            audio.currentTime = 0;
          }
        }, 50);
      } catch (_) {
        audio.pause();
      }
    };
  }, [onDone, onStartOpening]);

  const handleOpenNow = () => {
    playDoorAudio();
    if (!window.__veeduDoorsOpened) {
      window.__veeduDoorsOpened = true;
      window.dispatchEvent(new CustomEvent("veedu:doors-opened"));
    }
    if (!opening) {
      setOpening(true);
      if (onStartOpening) onStartOpening();
      setTimeout(() => {
        onDone();
      }, 5000);
    }
  };

  return (
    <motion.div
      data-testid="preloader"
      className="fixed inset-0 z-[180] bg-transparent overflow-hidden flex select-none cursor-pointer pointer-events-auto"
      style={{ perspective: "1500px" }}
      onClick={handleOpenNow}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
    >
      {/* Hidden audio element for browser-native media autoplay engine */}
      <audio
        src="/audio/freesound_community-restaurant-bell-101396.mp3"
        autoPlay
        playsInline
        onPlay={(e) => {
          e.currentTarget.playbackRate = 0.5;
        }}
        className="hidden"
      />

      {/* Left Door Leaf */}
      <motion.div
        className="h-full w-1/2 relative origin-left z-10 border-r border-black/40 shadow-2xl overflow-hidden bg-[#160F0B]"
        initial={{ x: "0%", rotateY: 0 }}
        animate={
          opening
            ? { x: "-101%", rotateY: -35 }
            : { x: "0%", rotateY: 0 }
        }
        transition={{ duration: 5.0, ease: [0.76, 0, 0.24, 1] }}
      >
        <div className="absolute top-0 left-0 w-[100vw] h-[100vh] pointer-events-none">
          <img src="/Images/door.png" alt="" className="w-full h-full object-cover object-center" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/50 pointer-events-none" />
      </motion.div>

      {/* Right Door Leaf */}
      <motion.div
        className="h-full w-1/2 relative origin-right z-10 border-l border-black/40 shadow-2xl overflow-hidden bg-[#160F0B]"
        initial={{ x: "0%", rotateY: 0 }}
        animate={
          opening
            ? { x: "101%", rotateY: 35 }
            : { x: "0%", rotateY: 0 }
        }
        transition={{ duration: 5.0, ease: [0.76, 0, 0.24, 1] }}
      >
        <div className="absolute top-0 right-0 w-[100vw] h-[100vh] pointer-events-none">
          <img src="/Images/door.png" alt="" className="w-full h-full object-cover object-center" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-l from-black/30 via-transparent to-black/50 pointer-events-none" />
      </motion.div>

      {/* Center Branding Overlay */}
      <motion.div
        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 pointer-events-none text-center px-4"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={
          opening
            ? { opacity: 0, scale: 1.1, y: -20 }
            : { opacity: 1, scale: 1, y: 0 }
        }
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <span className="font-mal text-3xl md:text-4xl text-brass drop-shadow-[0_4px_16px_rgba(0,0,0,0.9)]">
          വീട്
        </span>
        <span className="font-body text-[11px] md:text-xs tracking-[0.45em] text-cream/90 uppercase font-light drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
          Welcome Home
        </span>
        <span className="mt-2 px-3 py-1 rounded-full border border-brass/35 bg-black/65 text-brass text-[9px] md:text-[10.5px] font-body tracking-[0.25em] uppercase animate-pulse shadow-lg backdrop-blur-sm">
          Tap Anywhere To Enter
        </span>
      </motion.div>
    </motion.div>
  );
};

export default Preloader;
